import React from 'react';
import _ from 'underscore';

import { Keys } from '~commonInfra/utils/Keys';
import { Messages } from '~src/codegen/Localize';

import { FormikCheckbox } from '../../../commonInfra/components/FormikCheckbox';
import { IUserNotificationSettingsBucketGroup, IUserNotificationSettingsDimension, IUserNotificationSettingsInfo, IUserNotificationSettingWithUiInfo } from '../../models/IUserNotificationsSettingsInfo';
import * as styles from '../../styles/userNotificationSettingsStyles';
import { UserNotificationsSettingsFormUtils } from './UserNotificationsSettingsFormUtils';

export interface UserNotificationsSettingsGridProps {
  settings: IUserNotificationSettingsInfo;
}

/**
 * Component that renders the settings grid structure. Needs to be wrapped in a Formik context.
 */
export class UserNotificationsSettingsGrid extends React.PureComponent<UserNotificationsSettingsGridProps> {

  public constructor(props: UserNotificationsSettingsGridProps) {
    super(props);
  }

  public render() {
    const settings = this.props.settings;
    const channels = settings.channels;

    const groupSections = settings.groups
      .map(group => this.getSettingsForGroup(group, channels, settings.settings));

    return (
      <table>
        <thead>
        <tr>
          <th
            key={`channel-header-notification_option`}
            id={`channel-header-notification_option`}
            className={styles.headerCell}
          >{Messages.userSetting_Notification_Option_Header()}
          </th>
          {this.getChannelHeaders(this.props.settings.channels)}
        </tr>
        </thead>
        <tbody>
          {groupSections}
        </tbody>
      </table>
    );
  }

  private getChannelHeaders(channels: IUserNotificationSettingsDimension[]): JSX.Element[] {
    return _.sortBy(channels, 'order').map(c => {
      return (
        <th
          key={`channel-header-${c.id}`}
          id={`channel-header-${c.id}`}
          className={styles.headerCell}
          title={c.ui.hint}
        >
          {c.ui.label}
        </th>
      );
    });
  }

  private getSettingsForGroup(
    group: IUserNotificationSettingsBucketGroup,
    channels: IUserNotificationSettingsDimension[],
    settings: IUserNotificationSettingWithUiInfo[]
  ): JSX.Element[] {
    const settingsByKey = _.indexBy(settings, s => UserNotificationsSettingsFormUtils.toSettingKey(s));

    const groupHeaderId = `group-header-${group.id}`;
    const result: JSX.Element[] = group.notificationBuckets.map(bucket => {
      const bucketHeaderId = `bucket-header-${bucket.id}`;
      return (
        <tr key={'setting-row-' + bucket.id}>
          <th
            key={'setting-row-header-' + bucket.id}
            id={bucketHeaderId}
            className={styles.bucketNameCell}
            title={bucket.ui.hint}>{bucket.ui.label}
          </th>
          {channels.map(channel => {
            const settingKey = UserNotificationsSettingsFormUtils.toSettingKey({
              notificationBucket: bucket.id,
              channel: channel.id,
            });
            const setting = settingsByKey[settingKey];
            if (!setting) {
              return <td key={'empty-cell-' + settingKey}></td>;
            }

            const channelHeaderId = `channel-header-${channel.id}`;
            return (
              <td
                key={'setting-cell-' + settingKey}
                className={styles.settingCell}
                headers={`${groupHeaderId} ${bucketHeaderId} ${channelHeaderId}`}
              >
                <FormikCheckbox
                  key={'checkbox-' + settingKey}
                  className='un-settings-checkbox'
                  label={bucket.ui.label + ' ' + channel.ui.label} // TODO
                  // https://tableau.slack.com/archives/C9LU7TKEC/p1605069118225500
                  containerStyle={{width: '20px', height: '20px', display: 'inline-flex' }}
                  testId={settingKey}
                  name={settingKey}
                  disabled={setting.disabledByOverride}
                  onKeyDown={(e) => this.handleKeyDown(e, channel.id)}
                  data-channel-id={channel.id}
                />
              </td>
            );
          })}
        </tr>
      );
    });

    return [
      <tr key={'setting-row-group-' + group.id}>
        <td
          colSpan={channels.length + 1}
          id={groupHeaderId}
          className={styles.groupNameCell}
          title={group.ui.hint}
        >
          {group.ui.label}
        </td>
      </tr>
    ].concat(result);
  }

  private handleKeyDown(e: React.KeyboardEvent<HTMLElement>, channelId: string): void {
    if (![Keys.ArrowDown, Keys.ArrowUp, Keys.ArrowLeft, Keys.ArrowRight].includes(e.keyCode)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (e.keyCode === Keys.ArrowDown || e.keyCode === Keys.ArrowUp) {
      const navTo = e.keyCode === Keys.ArrowDown
        ? (el: Element) => el.nextElementSibling
        : (el: Element) => el.previousElementSibling;

      const parent = this.getParentNode(e.currentTarget, 'TR');

      let nextSibling = navTo(parent);
      let checkboxElement: HTMLElement;
      while (nextSibling && !checkboxElement) {
        checkboxElement = nextSibling.querySelector(`input[data-channel-id="${channelId}"]:not([disabled])`);
        nextSibling = navTo(nextSibling);
      }
      checkboxElement?.focus();

    } else if (e.keyCode === Keys.ArrowRight || e.keyCode === Keys.ArrowLeft) {
      const navTo = e.keyCode === Keys.ArrowRight
        ? (el: Element) => el.nextElementSibling
        : (el: Element) => el.previousElementSibling;

      const parent = this.getParentNode(e.currentTarget, 'TD');

      let nextSibling = navTo(parent);
      let checkboxElement: HTMLElement;
      while (nextSibling && !checkboxElement) {
        checkboxElement = nextSibling.querySelector('input:not([disabled])');
        nextSibling = navTo(nextSibling);
      }
      checkboxElement?.focus();
    }
  }

  private getParentNode(el: HTMLElement, parentNodeName: string) {
    let parent = el.parentElement;
    while (parent && parent.nodeName !== parentNodeName.toUpperCase()) {
      parent = parent.parentElement;
    }
    return parent;
  }

}
